import { CoreEvent, eventNames } from '@carvana/core-events';
import type { NavigationConfig } from '@carvana/header';
import { type MouseEvent, useCallback, useMemo } from 'react';
import { isHomePathname } from '~/constants/common/homeAppRouter';
import {
  COMPOSER_OPEN_PARAM,
  HEADER_NAV_ENTRY_POINT,
  isComposerExperimentPage
} from '~/constants/composer';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import useComposerOpenReady from '~/hooks/common/composer/useComposerOpenReady';

export const HEADER_SEARCH_COMPOSER_HREF = `/cars?refSource=header&${COMPOSER_OPEN_PARAM}=${HEADER_NAV_ENTRY_POINT}`;

export default function useHeaderNavigationConfig(
  pathname: string | null | undefined
): NavigationConfig | undefined {
  const showComposerTreatment = useShowComposerTreatment();
  const { whenReady } = useComposerOpenReady();

  const isComposerPage =
    isHomePathname(pathname) ||
    (pathname != null && isComposerExperimentPage(pathname));
  const isSrp = pathname?.startsWith('/cars') ?? false;
  const shouldSearchOpenComposer = showComposerTreatment && isComposerPage;

  const handleSearchClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;

      event.preventDefault();

      if (!isSrp) {
        window.location.assign(HEADER_SEARCH_COMPOSER_HREF);

        return;
      }

      whenReady(() => {
        CoreEvent.emit(eventNames.composer.open, {
          entryPoint: HEADER_NAV_ENTRY_POINT,
          source: 'page'
        });
      });
    },
    [isSrp, whenReady]
  );

  return useMemo(
    () =>
      shouldSearchOpenComposer
        ? { search: { onClick: handleSearchClick } }
        : undefined,
    [handleSearchClick, shouldSearchOpenComposer]
  );
}
